import { Account } from '@commutifi-fe/interfaces';
import { combineReducers } from '@reduxjs/toolkit';
import { AuthActionType } from '../auth/actions';
import * as authTypes from '../auth/types';
import { AccountActionTypes } from './actions';
import * as types from './types';
const byId = (state: Record<string, Account>, action: AccountActionTypes | AuthActionType): Record<string, Account | undefined> => {
  switch (action.type) {
    case types.FETCH_ME_SUCCESS:
      return {
        ...state,
        ...action.payload.entities.account
      };
    case types.FETCH_ACCOUNT_SUCCESS:
      {
        return {
          ...state,
          [action.response.result]: {
            ...state[action.response.result],
            ...action.response.entities.account[action.response.result]
          }
        };
      }
    case types.UPDATE_ACCOUNT_SUCCESS:
      {
        // Auth-mode (SSO vs password/OTP) is server-derived state that only `/me`
        // resolves authoritatively — a field patch's response must never write it
        // into the store, or a stale mutation-time classification would flip
        // onboarding (e.g. an SSO user changing their locale gets bounced to the
        // set-password step — #5022). Strip it here, the single point every
        // consumer reads through `byId`.
        const updatedEntity: Partial<Account> = action.response.entities.account[action.response.result] ?? {};
        const {
          authentication: _authentication,
          ...patchedAccount
        } = updatedEntity;
        return {
          ...state,
          [action.response.result]: {
            ...state[action.response.result],
            ...patchedAccount
          }
        };
      }
    case authTypes.LOGIN_SUCCESS:
      if (!action.account.id) {
        return state;
      }
      return {
        ...state,
        [action.account.id]: {
          ...state[action.account.id],
          ...action.account
        }
      };
    case authTypes.LOGOUT_SUCCESS:
      {
        const currentAccountId = action.payload?.currentAccountId;
        if (!currentAccountId || !state[currentAccountId]) {
          return state;
        }
        const {
          [currentAccountId]: _removed,
          ...remainingAccounts
        } = state;
        return remainingAccounts;
      }
    case types.FETCH_ME_REQUEST:
    case types.FETCH_ME_FAILURE:
    case types.FETCH_ACCOUNT_REQUEST:
    case types.FETCH_ACCOUNT_FAILURE:
    case types.UPDATE_ACCOUNT_REQUEST:
    case types.UPDATE_ACCOUNT_FAILURE:
    case types.SET_SUPER_ADMIN_VIEW_ACCOUNT_ID:
    case authTypes.LOGIN_REQUEST:
    case authTypes.LOGIN_FAILURE:
    case authTypes.LOGOUT:
    case authTypes.UNAUTHORIZE_USER:
    case authTypes.CLEAR_USER:
    default:
      return state || {};
  }
};
const currentId = (state: string | null, action: AccountActionTypes | AuthActionType): string | null => {
  switch (action.type) {
    case authTypes.LOGIN_SUCCESS:
      return action.account.id || null;
    case types.FETCH_ME_SUCCESS:
      return action.payload.result;
    case authTypes.LOGOUT_SUCCESS:
    case authTypes.UNAUTHORIZE_USER:
    case authTypes.CLEAR_USER:
      return null;
    case types.FETCH_ME_REQUEST:
    case types.FETCH_ME_FAILURE:
    case types.FETCH_ACCOUNT_REQUEST:
    case types.FETCH_ACCOUNT_SUCCESS:
    case types.FETCH_ACCOUNT_FAILURE:
    case types.UPDATE_ACCOUNT_REQUEST:
    case types.UPDATE_ACCOUNT_SUCCESS:
    case types.UPDATE_ACCOUNT_FAILURE:
    case types.SET_SUPER_ADMIN_VIEW_ACCOUNT_ID:
    case authTypes.LOGIN_REQUEST:
    case authTypes.LOGIN_FAILURE:
    case authTypes.LOGOUT:
    default:
      return state || null;
  }
};
const superAdminViewAccountId = (state: string | null, action: AccountActionTypes | AuthActionType) => {
  switch (action.type) {
    case types.FETCH_ACCOUNT_SUCCESS:
      return action.response.result;
    case types.SET_SUPER_ADMIN_VIEW_ACCOUNT_ID:
      return action.payload;
    case authTypes.LOGOUT_SUCCESS:
    case authTypes.UNAUTHORIZE_USER:
    case authTypes.CLEAR_USER:
      return null;
    case types.FETCH_ME_REQUEST:
    case types.FETCH_ME_SUCCESS:
    case types.FETCH_ME_FAILURE:
    case types.FETCH_ACCOUNT_REQUEST:
    case types.FETCH_ACCOUNT_FAILURE:
    case types.UPDATE_ACCOUNT_REQUEST:
    case types.UPDATE_ACCOUNT_SUCCESS:
    case types.UPDATE_ACCOUNT_FAILURE:
    case authTypes.LOGIN_REQUEST:
    case authTypes.LOGIN_SUCCESS:
    case authTypes.LOGIN_FAILURE:
    case authTypes.LOGOUT:
    default:
      return state || null;
  }
};
const isFetching = (state: boolean, action: AccountActionTypes | AuthActionType) => {
  switch (action.type) {
    case types.FETCH_ME_REQUEST:
    case types.FETCH_ACCOUNT_REQUEST:
      return true;
    case types.FETCH_ME_FAILURE:
    case types.FETCH_ACCOUNT_FAILURE:
    case types.FETCH_ME_SUCCESS:
    case types.FETCH_ACCOUNT_SUCCESS:
    case authTypes.LOGOUT_SUCCESS:
      return false;
    case types.UPDATE_ACCOUNT_REQUEST:
    case types.UPDATE_ACCOUNT_SUCCESS:
    case types.UPDATE_ACCOUNT_FAILURE:
    case types.SET_SUPER_ADMIN_VIEW_ACCOUNT_ID:
    case authTypes.LOGIN_REQUEST:
    case authTypes.LOGIN_SUCCESS:
    case authTypes.LOGIN_FAILURE:
    case authTypes.LOGOUT:
    case authTypes.UNAUTHORIZE_USER:
    case authTypes.CLEAR_USER:
    default:
      return state ?? false;
  }
};
const error = (state: any, action: AccountActionTypes | AuthActionType) => {
  switch (action.type) {
    case types.FETCH_ME_SUCCESS:
      return null;
    case types.FETCH_ME_FAILURE:
      return action.error;
    case authTypes.LOGOUT_SUCCESS:
    case authTypes.UNAUTHORIZE_USER:
    case authTypes.CLEAR_USER:
      // Clear any account errors when user is logged out or unauthorized
      return null;
    case types.FETCH_ME_REQUEST:
    case types.FETCH_ACCOUNT_REQUEST:
    case types.FETCH_ACCOUNT_SUCCESS:
    case types.FETCH_ACCOUNT_FAILURE:
    case types.UPDATE_ACCOUNT_REQUEST:
    case types.UPDATE_ACCOUNT_SUCCESS:
    case types.UPDATE_ACCOUNT_FAILURE:
    case types.SET_SUPER_ADMIN_VIEW_ACCOUNT_ID:
    case authTypes.LOGIN_REQUEST:
    case authTypes.LOGIN_SUCCESS:
    case authTypes.LOGIN_FAILURE:
    case authTypes.LOGOUT:
    default:
      return state || null;
  }
};
export const accountsReducer = combineReducers({
  byId,
  currentId,
  superAdminViewAccountId,
  isFetching,
  error
});
export type AccountState = ReturnType<typeof accountsReducer>;
export default accountsReducer;